/** @jsxImportSource @emotion/react */
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { Box, Chip, IconButton, TableCell, TableRow, Typography } from '@mui/material'
import {
  cellStyles,
  chipStyles,
  iconButtonStyles,
  labelStyles,
  rowStyles,
} from '../styles/HazardGroupRow.styles'
import { LayerSuitabilityRecord } from '@app/domains/suitability/types'
import { GRADE_CONFIG } from '@app/domains/suitability/utils/suitability.utils'

type BaseGroupRowProps = {
  label: string
  count?: number
  colSpan: number
  isExpanded: boolean
  onToggle: () => void
  owner?: string | null
  confidenceRating?: string | null
  suitability: LayerSuitabilityRecord[] | null
}

export function HazardGroupRow({
  label,
  count,
  colSpan,
  isExpanded,
  onToggle,
  owner,
  confidenceRating,
  suitability,
}: BaseGroupRowProps) {
  return (
    <TableRow onClick={onToggle} css={rowStyles}>
      <TableCell colSpan={colSpan} css={cellStyles}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body1" fontWeight={700} css={labelStyles}>
              <IconButton size="small" disableRipple tabIndex={-1} css={iconButtonStyles}>
                {isExpanded ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}
              </IconButton>
              {label}
              <Chip label={count} size="small" variant="outlined" css={chipStyles} />
            </Typography>

            {(owner || confidenceRating) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, pr: 1 }}>
                {owner && (
                  <Typography variant="body1" color="text.secondary">
                    <Typography
                      component="span"
                      variant="body1"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      Owner:
                    </Typography>{' '}
                    {owner}
                  </Typography>
                )}
                {confidenceRating && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body1" fontWeight={600} color="text.secondary">
                      Data Confidence:
                    </Typography>
                    <Chip
                      label={confidenceRating}
                      size="small"
                      variant="filled"
                      sx={{ color: 'white' }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {suitability && suitability.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4.5 }}>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                Suitability:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                {suitability.map((item) => {
                  const grade = GRADE_CONFIG[item.suitability_grade] ?? GRADE_CONFIG.C
                  return (
                    <Box
                      key={item.use_case_slug}
                      title={grade.label}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        background: grade.bg,
                        color: grade.color,
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: 'default',
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: grade.color,
                        }}
                      />
                      {item.use_case_name} ({item.suitability_grade})
                    </Box>
                  )
                })}
              </Box>
            </Box>
          )}
        </Box>
      </TableCell>
    </TableRow>
  )
}
